import React, { useEffect } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  InputNumber,
  Space,
  Card,
  Row,
  Col
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";

/**
 * ClientForm
 *
 * Props:
 * - mode: "create" | "edit"
 *     Controls the form's labeling. Defaults to "create".
 * - initialValues: object
 *     Optional. If provided, pre-populates the form for editing.
 * - onSubmit: function(payload)
 *     Callback when the user submits. Receives a payload
 *     shaped like ClientDocument (minus server-generated fields).
 */
export const ClientForm = ({
  mode = "create",
  initialValues = {},
  onSubmit
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    // If we have initial values (edit mode), populate the form
    form.resetFields();
    form.setFieldsValue(transformForForm(initialValues));
  }, [initialValues, form]);

  /**
   * AntD stores dates as Moment objects, so we might need to convert
   * them from raw ISO strings (if that's how your data is stored).
   */
  const transformForForm = (vals) => {
    const copy = { ...vals };
    // If there's a dob value, convert to a Moment
    if (copy?.client_details?.dob) {
      copy.client_details.dob = moment(copy.client_details.dob);
    }
    return copy;
  };

  /**
   * Called when the user clicks "Submit" and validation passes.
   */
  const handleFinish = (values) => {
    // Convert date fields to a suitable string or timestamp
    if (values.client_details?.dob) {
      values.client_details.dob = values.client_details.dob.toISOString();
    }

    // Build final payload matching ClientDocument shape
    const payload = {
      client_id: initialValues.client_id || "", // If editing, preserve existing client_id
      advisor_uid: initialValues.advisor_uid || "", // Or set via server
      client_details: {
        name: values.client_details.name,
        email: values.client_details.email,
        phone: values.client_details.phone,
        household_id: values.client_details.household_id || null,
        dob: values.client_details.dob || null
      },
      // Removed portfolios and any reference to blacklists here
      custodians: values.custodians || [],
      account_ids: initialValues.account_ids || [],
      preferences: {
        risk_score: values.preferences?.risk_score ?? null,
        target_equity: values.preferences?.target_equity ?? null,
        target_fixed: values.preferences?.target_fixed ?? null,
        min_cash: values.preferences?.min_cash ?? null,
        distribution_preferences:
          values.preferences?.distribution_preferences || {}
      },
      portfolio_metrics: values.portfolio_metrics || null,
      ext_ids: values.ext_ids || {},
      status: values.status || "active",
      addresses: values.addresses || [],
      tags: values.tags || {},
      notes: values.notes || ""
    };

    // Fire the parent callback
    onSubmit(payload);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      // Provide some default initial values for create mode
      initialValues={{
        status: "active"
      }}
    >
      {/* CLIENT DETAILS */}
      <Card title="Client Details" style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Full Name"
              name={["client_details", "name"]}
              rules={[{ required: true, message: "Please enter the name" }]}
            >
              <Input placeholder="Client's full name" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Email"
              name={["client_details", "email"]}
              rules={[
                { required: true, message: "Please enter the email" },
                { type: "email", message: "Invalid email format" }
              ]}
            >
              <Input placeholder="Primary email address" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Phone"
              name={["client_details", "phone"]}
              rules={[{ required: true, message: "Please enter the phone" }]}
            >
              <Input placeholder="Primary phone number" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Household ID"
              name={["client_details", "household_id"]}
            >
              <Input placeholder="Optional household/grouping ID" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Date of Birth" name={["client_details", "dob"]}>
              <DatePicker
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
                disabledDate={(current) =>
                  current && current > moment().endOf("day")
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* CUSTODIANS */}
      <Card title="Custodians" style={{ marginBottom: 24 }}>
        <Form.List name="custodians">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space
                  key={key}
                  style={{ display: "flex", marginBottom: 8 }}
                  align="baseline"
                >
                  <Form.Item
                    {...restField}
                    label="Custodian Name"
                    name={[name, "custodian_name"]}
                  >
                    <Input placeholder="e.g. Fidelity, Schwab" />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    label="Account #"
                    name={[name, "account_number"]}
                  >
                    <Input placeholder="Account number" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                >
                  Add Custodian
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Card>

      {/* Removed entire "Portfolios" section here */}

      {/* PREFERENCES */}
      <Card title="Preferences" style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              label="Riskalyze Score"
              name={["preferences", "risk_score"]}
            >
              <InputNumber style={{ width: "100%" }} min={0} max={100} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Target Equity %"
              name={["preferences", "target_equity"]}
            >
              <InputNumber style={{ width: "100%" }} min={0} max={100} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Target Fixed %"
              name={["preferences", "target_fixed"]}
            >
              <InputNumber style={{ width: "100%" }} min={0} max={100} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Min Cash" name={["preferences", "min_cash"]}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* PORTFOLIO METRICS */}
      <Card title="Portfolio Metrics" style={{ marginBottom: 24 }}>
        <Form.Item
          label="Total Value"
          name={["portfolio_metrics", "total_value"]}
        >
          <InputNumber style={{ width: "100%" }} min={0} step={1000} />
        </Form.Item>
      </Card>

      {/* ADDRESSES */}
      <Card title="Addresses" style={{ marginBottom: 24 }}>
        <Form.List name="addresses">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space
                  key={key}
                  style={{ display: "flex", marginBottom: 8 }}
                  align="baseline"
                >
                  <Form.Item {...restField} label="Type" name={[name, "type"]}>
                    <Input placeholder="Home, Mailing, etc." />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    label="Line 1"
                    name={[name, "line1"]}
                  >
                    <Input placeholder="Address line 1" />
                  </Form.Item>
                  <Form.Item {...restField} label="City" name={[name, "city"]}>
                    <Input placeholder="City" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                >
                  Add Address
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Card>

      {/* ADDITIONAL FIELDS */}
      <Card title="Additional Info" style={{ marginBottom: 24 }}>
        <Form.Item label="Status" name="status">
          <Input placeholder="active, inactive, archived, etc." />
        </Form.Item>
        <Form.Item label="Notes" name="notes">
          <Input.TextArea rows={3} />
        </Form.Item>
      </Card>

      {/* SUBMIT BUTTON */}
      <Form.Item>
        <Button type="primary" htmlType="submit">
          {mode === "edit" ? "Update Client" : "Create Client"}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ClientForm;
